/**
 * SessionExpiredHandler — listens for "session-expired" events fired by apiFetch
 * and shows a toast + redirects to /sign-in.
 *
 * Also bridges the toast context to the global MutationCache error handler
 * so mutation errors show toast notifications.
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "./toast.js";
import { setGlobalToast } from "../main.js";

export function SessionExpiredHandler() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const hasRedirected = useRef(false);

  // Bridge toast context to global MutationCache error handler
  useEffect(() => {
    setGlobalToast(toast);
  }, [toast]);

  useEffect(() => {
    function handleSessionExpired() {
      // Only redirect once to avoid multiple toasts/redirects
      if (hasRedirected.current) return;
      hasRedirected.current = true;

      toast("warning", "Your session has expired. Please sign in again.");
      qc.clear();

      // Preserve current URL for return-after-login
      const returnTo = window.location.pathname + window.location.search;
      navigate(`/sign-in${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`);

      // Reset after a short delay to allow re-triggering if the user
      // navigates back to a protected page
      setTimeout(() => {
        hasRedirected.current = false;
      }, 5000);
    }

    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, [navigate, qc, toast]);

  return null;
}
